import { createGamepadPoll } from '../../../utils/input/gamepadPoll';
import { inputSource } from '../../../utils/input/inputSource';
import { controllerMode } from '../../../utils/input/controllerMode';
import { matchActionFromGamepad, isHoldAction } from '../../../utils/input/gamepadActions';
import { GAMEPAD_BUTTON, RADIAL_ACTIONS } from '../../../utils/game/keybindings';
import { selectedSliceForStick } from '../RadialActionMenu';

// Gamepad / controller wiring for the game board, extracted from
// GameBoard.jsx. The controller drives the existing mouse pipeline: the
// right stick integrates into an aim point that synthesizes mousemove
// events, so hover/outline/presence side effects stay correct for free.
//
// Host contract: canvasRef, scene, meshes, hoveredMesh, sync, state,
// setState, forceUpdate, handleMouseMove, dispatchHotkeyAction,
// _presenceHover, handlePileSearchClose, _unmounted, plus the
// controller* instance fields initialised in the GameBoard constructor
// (controllerPoll, controllerListeners, controllerStick, controllerAim,
// controllerHeldButtons, controllerRadialOpen/Index,
// controllerGrabbedCard/Origin, controllerRAF, inputSourceState).

// ---------- Controller wiring ---------------------------------------
export function initController(host) {
  if (typeof window === 'undefined') return;

  // Claim controller input for the board while this component is
  // mounted. The app-level focus bridge stands down whenever the
  // mode is anything other than 'menu'.
  controllerMode.set('board');

  host.controllerPoll = createGamepadPoll({ padIndex: 0 });

  const off1 = host.controllerPoll.on('button-down', (e) => host.handleGamepadButtonDown(e.detail));
  const off2 = host.controllerPoll.on('button-up',   (e) => host.handleGamepadButtonUp(e.detail));
  const off3 = host.controllerPoll.on('axis',        (e) => host.handleGamepadAxis(e.detail));
  host.controllerListeners.push(off1, off2, off3);

  host.controllerPoll.start();

  host.unsubInputSource = inputSource.subscribe((source) => {
    const prev = host.inputSourceState;
    host.inputSourceState = source;
    // Leaving gamepad mode clears transient overlays so a sudden
    // mouse jiggle never leaves the reticle / radial hanging.
    if (prev === 'gamepad' && source !== 'gamepad') {
      host.controllerRadialOpen = false;
      host.controllerRadialIndex = -1;
      if (host.controllerGrabbedCard) controllerCancelGrab(host);
    }
    host.forceUpdate();
  });

  // Bridge mouse / keyboard events into inputSource so the state
  // machine tracks the most recent device. Idempotent. These
  // dedupe internally.
  host._controllerMouseReporter = () => inputSource.report('mouse');
  host._controllerKeyReporter = () => inputSource.report('keyboard');
  window.addEventListener('mousemove', host._controllerMouseReporter, true);
  window.addEventListener('mousedown', host._controllerMouseReporter, true);
  window.addEventListener('keydown', host._controllerKeyReporter, true);

  host._controllerVisibilityHandler = () => {
    host.controllerPoll?.onVisibilityChange(document.hidden);
  };
  document.addEventListener('visibilitychange', host._controllerVisibilityHandler);

  // Seed aim at the canvas centre so the reticle appears in a sane
  // place the first time the user touches the stick.
  const rect = host.canvasRef.current?.getBoundingClientRect();
  if (rect) {
    host.controllerAim.x = rect.left + rect.width / 2;
    host.controllerAim.y = rect.top + rect.height / 2;
  }

  const tick = () => {
    if (host._unmounted) return;
    controllerTick(host);
    host.controllerRAF = requestAnimationFrame(tick);
  };
  host.controllerRAF = requestAnimationFrame(tick);
}

export function teardownController(host) {
  // Release the 'board' claim so the app-level focus bridge takes
  // over again when the match ends.
  controllerMode.set('menu');

  if (host.controllerRAF !== null) cancelAnimationFrame(host.controllerRAF);
  host.controllerRAF = null;
  if (host.controllerPoll) {
    host.controllerPoll.stop();
    host.controllerPoll = null;
  }
  for (const off of host.controllerListeners) off();
  host.controllerListeners = [];
  if (host.unsubInputSource) { host.unsubInputSource(); host.unsubInputSource = null; }
  if (host._controllerMouseReporter) {
    window.removeEventListener('mousemove', host._controllerMouseReporter, true);
    window.removeEventListener('mousedown', host._controllerMouseReporter, true);
    host._controllerMouseReporter = null;
  }
  if (host._controllerKeyReporter) {
    window.removeEventListener('keydown', host._controllerKeyReporter, true);
    host._controllerKeyReporter = null;
  }
  if (host._controllerVisibilityHandler) {
    document.removeEventListener('visibilitychange', host._controllerVisibilityHandler);
    host._controllerVisibilityHandler = null;
  }
}

export function handleGamepadButtonDown(host, { index, repeat }) {
  inputSource.report('gamepad');
  host.controllerHeldButtons.add(index);

  // RT opens the radial menu. Hold-to-open pattern, so we track
  // state locally instead of dispatching an action. Slice selection
  // is refreshed on every stick axis change.
  if (index === GAMEPAD_BUTTON.RT) {
    host.controllerRadialOpen = true;
    host.controllerRadialIndex = selectedSliceForStick(
      host.controllerStick.rx,
      host.controllerStick.ry,
    );
    host.forceUpdate();
    return;
  }

  const actionId = matchActionFromGamepad(index);
  if (!actionId) return;

  // Hold-style actions: dispatch on press, no repeat. Matched up
  // with a button-up handler below.
  if (isHoldAction(index)) {
    dispatchControllerAction(host, actionId);
    return;
  }

  if (repeat) {
    // D-pad repeat. Skip modifier guarding, just re-fire the action.
    dispatchControllerAction(host, actionId);
    return;
  }

  dispatchControllerAction(host, actionId);
}

export function handleGamepadButtonUp(host, { index }) {
  host.controllerHeldButtons.delete(index);

  if (index === GAMEPAD_BUTTON.RT && host.controllerRadialOpen) {
    const slice = host.controllerRadialIndex;
    host.controllerRadialOpen = false;
    host.controllerRadialIndex = -1;
    if (slice >= 0 && slice < RADIAL_ACTIONS.length) {
      dispatchControllerAction(host, RADIAL_ACTIONS[slice]);
    }
    host.forceUpdate();
    return;
  }

  // Release-close for hold actions (inspect). Release also disarms a
  // hover-less press so the next hovered card does not open by itself.
  if (index === GAMEPAD_BUTTON.LT) {
    host.inspectorHold.noteClose();
    if (!host.state.inspectedCard) return;
    host.setState({ inspectedCard: null, inspectorStack: [] });
    host.scene?.setPaused(false);
    host._presenceHover(host.hoveredMesh?.userData?.cardInstance?.id || null);
  }
}

export function handleGamepadAxis(host, { axes }) {
  host.controllerStick.lx = axes[0];
  host.controllerStick.ly = axes[1];
  host.controllerStick.rx = axes[2];
  host.controllerStick.ry = axes[3];
  inputSource.report('gamepad');

  // Update radial highlight live while RT is held.
  if (host.controllerRadialOpen) {
    const next = selectedSliceForStick(host.controllerStick.rx, host.controllerStick.ry);
    if (next !== host.controllerRadialIndex) {
      host.controllerRadialIndex = next;
      host.forceUpdate();
    }
  }

  // Left-stick pan. Drive the existing camera-key-held model used
  // by keyboard WASD, with the stick magnitude gating "held" state.
  const panThreshold = 0.35;
  const lx = host.controllerStick.lx;
  const ly = host.controllerStick.ly;
  host.scene?.setKeyHeld('w', ly < -panThreshold);
  host.scene?.setKeyHeld('s', ly >  panThreshold);
  host.scene?.setKeyHeld('a', lx < -panThreshold);
  host.scene?.setKeyHeld('d', lx >  panThreshold);
}

// Per-frame loop that integrates right-stick into the aim point and
// synthesizes a mousemove to drive the existing hover pipeline.
export function controllerTick(host) {
  if (host.inputSourceState !== 'gamepad') return;
  const canvas = host.canvasRef.current;
  if (!canvas) return;

  let rx = host.controllerStick.rx;
  let ry = host.controllerStick.ry;
  // Second-layer dead zone. The SDL driver already filters drift at
  // 0.15, but reticles are unforgiving. Anything that slips
  // through here would walk the aim over minutes. Cheap to re-check.
  const rmag = Math.hypot(rx, ry);
  if (rmag < 0.12) { rx = 0; ry = 0; }
  const rect = canvas.getBoundingClientRect();

  if (rx !== 0 || ry !== 0) {
    // Sensitivity: ~900 px/sec at full deflection. A 60 fps frame at
    // full tilt moves ~15 px; feels brisk without blowing past cards
    // in a fan. Slow down to 0.4× when sticky-aim is snapped.
    const snapped = Boolean(host.hoveredMesh);
    const speed = snapped ? 360 : 900;
    const dt = 1 / 60;
    host.controllerAim.x += rx * speed * dt;
    host.controllerAim.y += ry * speed * dt;

    if (host.controllerAim.x < rect.left) host.controllerAim.x = rect.left;
    if (host.controllerAim.x > rect.right) host.controllerAim.x = rect.right;
    if (host.controllerAim.y < rect.top) host.controllerAim.y = rect.top;
    if (host.controllerAim.y > rect.bottom) host.controllerAim.y = rect.bottom;

    // Feed the existing hover pipeline. Every side effect (outline,
    // emissive, sync.hoverCard, cursor) stays correct because we
    // reuse handleMouseMove verbatim.
    const synthetic = {
      clientX: host.controllerAim.x,
      clientY: host.controllerAim.y,
      preventDefault() {},
      stopPropagation() {},
      _isController: true,
    };
    try { host.handleMouseMove(synthetic); } catch (_) {}

    host.forceUpdate();
  }

  // Carry the grabbed card with the aim point. We reuse the physics
  // drag pipeline by calling into scene.raycastTablePoint to find
  // the table-space target, then moving the card body toward it.
  if (host.controllerGrabbedCard) {
    const point = host.scene?.raycastTablePoint({
      clientX: host.controllerAim.x,
      clientY: host.controllerAim.y,
    });
    if (point) {
      const mesh = host.meshes.get(host.controllerGrabbedCard.id);
      if (mesh) {
        mesh.position.x = point.x;
        mesh.position.z = point.z;
      }
    }
  }
}

export function controllerStartGrab(host) {
  const hovered = host.hoveredMesh;
  if (!hovered || hovered.userData?.type !== 'card') return;
  const card = hovered.userData.cardInstance;
  if (!card) return;
  // Remember original transform so `cancel` can restore it.
  host.controllerGrabbedCard = card;
  host.controllerGrabbedOrigin = {
    x: hovered.position.x,
    y: hovered.position.y,
    z: hovered.position.z,
  };
}

export function controllerDropGrab(host) {
  const card = host.controllerGrabbedCard;
  host.controllerGrabbedCard = null;
  host.controllerGrabbedOrigin = null;
  if (!card) return;
  const mesh = host.meshes.get(card.id);
  if (!mesh) return;
  // Let the existing state-sync flow commit the new position.
  try {
    host.sync?.moveCard?.(card, { x: mesh.position.x, y: mesh.position.y, z: mesh.position.z });
  } catch (_) {}
}

export function controllerCancelGrab(host) {
  const card = host.controllerGrabbedCard;
  const origin = host.controllerGrabbedOrigin;
  host.controllerGrabbedCard = null;
  host.controllerGrabbedOrigin = null;
  if (!card || !origin) return;
  const mesh = host.meshes.get(card.id);
  if (mesh) {
    mesh.position.x = origin.x;
    mesh.position.y = origin.y;
    mesh.position.z = origin.z;
  }
}

// Dispatch an action ID originating from the controller. Actions
// requiring an event-shaped argument get a no-op stand-in so the
// existing switch can call preventDefault() safely.
export function dispatchControllerAction(host, actionId) {
  if (actionId === 'grab') {
    if (host.controllerGrabbedCard) controllerDropGrab(host);
    else controllerStartGrab(host);
    host.forceUpdate();
    return;
  }
  if (actionId === 'cancel') {
    if (host.controllerGrabbedCard) { controllerCancelGrab(host); host.forceUpdate(); return; }
    if (host.state.inspectedCard) {
      host.setState({ inspectedCard: null, inspectorStack: [] });
      host.scene?.setPaused(false);
      return;
    }
    if (host.state.searchPile) { host.handlePileSearchClose(); return; }
    if (host.state.showShortcutHelp) { host.setState({ showShortcutHelp: false }); return; }
    return;
  }
  const syntheticEvent = { preventDefault() {}, stopPropagation() {}, _isController: true };
  try { host.dispatchHotkeyAction(actionId, syntheticEvent); } catch (_) {}
}
